import React, { useCallback, useState } from 'react';
import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Divider,
  Form,
  Input,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { AlarmChannelOptions } from '@utils/alarmsConstants';
import { CustomSpace, StyledMinusIcon } from './styles';

export function ChannelField() {
  const { t } = useTranslation('create-alarm-step');
  const [open, setOpen] = useState(false);
  const [notificationChannels, setNotificationChannels] = useState<string[]>(
    [],
  );
  const notificationOptions = [
    {
      value: AlarmChannelOptions.TELEGRAM,
      label: t('create-alarm-step:stepTwo.createAlarmChannelOptionTelegram'),
    },
    {
      value: AlarmChannelOptions.EMAIL,
      label: t('create-alarm-step:stepTwo.createAlarmChannelOptionEmail'),
    },
  ];
  const form = Form.useFormInstance();
  const handleNotificationChannelChange = useCallback(
    (values: string[]) => {
      setNotificationChannels(values);

      if (values.includes('telegram') && !form.getFieldValue('telegramIds')) {
        form.setFieldsValue({ telegramIds: [''] });
      }

      if (values.includes('email') && !form.getFieldValue('emails')) {
        form.setFieldsValue({ emails: [''] });
      }
    },
    [form],
  );

  return (
    <>
      <Form.Item
        name="notificationChannels"
        label={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {t('create-alarm-step:stepTwo.createAlarmChannelField')}
            <Tooltip
              title={t('create-alarm-step:stepTwo.createAlarmChannelTooltip')}
              trigger="hover"
            >
              <Button type="link" icon={<QuestionCircleOutlined />} />
            </Tooltip>
          </div>
        }
        rules={[
          {
            required: true,
            message: t('create-alarm-step:stepTwo.createAlarmChannelRequired'),
          },
        ]}
      >
        <Select
          mode="multiple"
          options={notificationOptions}
          value={notificationChannels}
          open={open}
          onDropdownVisibleChange={visible => setOpen(visible)}
          onChange={handleNotificationChannelChange}
          onSelect={val => {
            const newValues = [...notificationChannels, val];
            setNotificationChannels(newValues);
            handleNotificationChannelChange(newValues);
            setOpen(false);
          }}
        />
      </Form.Item>

      {notificationChannels.includes('telegram') && (
        <>
          <Divider orientation="left">
            <Typography.Text strong>Telegram</Typography.Text>
          </Divider>
          <Form.List name="telegramIds">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Form.Item
                    required
                    key={field.key}
                    label={
                      index === 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {t(
                            'create-alarm-step:stepTwo.createAlarmChannelTelegramField',
                          )}
                          <Tooltip
                            title={t(
                              'create-alarm-step:stepTwo.createAlarmChannelTelegramTooltip',
                            )}
                            trigger="hover"
                          >
                            <Button
                              type="link"
                              icon={<QuestionCircleOutlined />}
                            />
                          </Tooltip>
                        </div>
                      ) : (
                        ''
                      )
                    }
                  >
                    <CustomSpace
                      align="baseline"
                      style={{ display: 'flex', marginBottom: 8 }}
                    >
                      <Form.Item
                        {...field}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: t(
                              'create-alarm-step:stepTwo.createAlarmChannelTelegramRequired',
                            ),
                          },
                        ]}
                        noStyle
                      >
                        <Input />
                      </Form.Item>
                      {fields.length > 1 ? (
                        <StyledMinusIcon onClick={() => remove(field.name)} />
                      ) : null}
                    </CustomSpace>
                  </Form.Item>
                ))}
                <Form.Item>
                  <Button
                    type="primary"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    style={{ width: '100%' }}
                  >
                    {t(
                      'create-alarm-step:stepTwo.createAlarmChannelTelegramAddField',
                    )}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </>
      )}

      {notificationChannels.includes('email') && (
        <>
          <Divider orientation="left">
            <Typography.Text strong>Email</Typography.Text>
          </Divider>
          <Form.List name="emails">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Form.Item
                    required
                    key={field.key}
                    label={
                      index === 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {t(
                            'create-alarm-step:stepTwo.createAlarmChannelEmailField',
                          )}
                          <Tooltip
                            title={t(
                              'create-alarm-step:stepTwo.createAlarmChannelEmailTooltip',
                            )}
                            trigger="hover"
                          >
                            <Button
                              type="link"
                              icon={<QuestionCircleOutlined />}
                            />
                          </Tooltip>
                        </div>
                      ) : (
                        ''
                      )
                    }
                  >
                    <CustomSpace
                      align="baseline"
                      style={{ display: 'flex', marginBottom: 8 }}
                    >
                      <Form.Item
                        {...field}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: t(
                              'create-alarm-step:stepTwo.createAlarmChannelEmailRequired',
                            ),
                          },
                          {
                            type: 'email',
                            message: t(
                              'create-alarm-step:stepTwo.createAlarmChannelEmailInvalidField',
                            ),
                          },
                        ]}
                        noStyle
                      >
                        <Input />
                      </Form.Item>
                      {fields.length > 1 ? (
                        <StyledMinusIcon onClick={() => remove(field.name)} />
                      ) : null}
                    </CustomSpace>
                  </Form.Item>
                ))}
                <Form.Item>
                  <Button
                    type="primary"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    style={{ width: '100%' }}
                  >
                    {t(
                      'create-alarm-step:stepTwo.createAlarmChannelEmailAddField',
                    )}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </>
      )}
    </>
  );
}
